import { z } from 'zod';
import { Id, IsoDateTime, Currency } from './common.js';

export const ProductSource = z.enum(['platform', 'direct']);

export const Product = z.object({
  product_id: Id,
  merchant_id: Id,
  source: ProductSource,
  external_product_id: z.string().nullable(),
  title: z.string(),
  description: z.string().default(''),
  tags: z.array(z.string()).default([]),
  category: z.string().nullable(),
  base_price_cents: z.number().int().nonnegative(),
  currency: Currency,
  status: z.enum(['active', 'draft', 'archived', 'deleted']).default('active'),
  primary_image_url: z.string().nullable(),
  embedding_state: z
    .enum(['pending', 'embedding', 'embedded', 'failed'])
    .default('pending'),
  embedding_model_version: z.string().nullable(),
  last_synced_at: IsoDateTime.nullable(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type Product = z.infer<typeof Product>;

export const ProductVariant = z.object({
  variant_id: Id,
  product_id: Id,
  merchant_id: Id,
  variant_name: z.string(),
  variant_attributes: z.record(z.string()).default({}),
  price_cents: z.number().int().nonnegative(),
  stock_count: z.number().int().default(0),
  low_stock_threshold: z.number().int().nonnegative().default(0),
  external_variant_id: z.string().nullable(),
});

export const ProductImage = z.object({
  image_id: Id,
  product_id: Id,
  merchant_id: Id,
  r2_key: z.string().nullable(),
  url: z.string().nullable(),
  display_order: z.number().int().nonnegative().default(0),
  is_primary: z.boolean().default(false),
});

export const PriceBucket = z.enum([
  'under_5',
  '5_15',
  '15_50',
  '50_150',
  '150_500',
  'over_500',
]);

export function priceBucket(priceCents: number, currency: 'USD' | 'IQD'): z.infer<typeof PriceBucket> {
  // Buckets defined in USD; for IQD divide by ~1310
  const usd = currency === 'IQD' ? priceCents / 100 / 1310 : priceCents / 100;
  if (usd < 5) return 'under_5';
  if (usd < 15) return '5_15';
  if (usd < 50) return '15_50';
  if (usd < 150) return '50_150';
  if (usd < 500) return '150_500';
  return 'over_500';
}
